import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Routes, Route } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Typography, 
  Box
} from '@mui/material';
import LoadingSkeleton from '../common/LoadingSkeleton';
import { courseService } from '../../services/api';
import './CoursePreview.css';
import CourseHeader from './CourseHeader';
import CoursePrerequisites from './CoursePrerequisites';
import CourseDescription from './CourseDescription';
import CourseModules from './CourseModules';
import CourseEnrollment from './CourseEnrollment';
import CourseDetails from './CourseDetails';
import AllInstances from './AllInstances';
import PageTabs from '../shared/PageTabs';
import { buildCourseTabs } from '../shared/tabConfigs';
import { useUser } from '../../context/UserContext';
const CoursePreview = () => {
  const { id, instanceId } = useParams();  
  const location = useLocation();  
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  let courseId = id;
  const { user } = useUser();
  const courseTabs = buildCourseTabs(courseId, { permissions: user?.permissions || [], roleId: user?.role_id }); 

  
  
  const refreshCourseData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  /*
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);        
        const response = await courseService.getCoursePreview(courseId, instanceId);
        setCourseData(response.data);
      } catch (err) {
        console.error('Error fetching course preview:', err);
        setError('Failed to load course preview');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, instanceId, refreshKey]);
  */

  useEffect(() => {
      let ignore = false;

      const fetchCourseData = async () => {
        try {
          setLoading(true);        
          //const response = await courseService.getCoursePreview(courseId, instanceId);
          const response = await courseService.getCoursePreview(courseId);
          if (!ignore) {
            setCourseData(response.data);
          }
        } catch (err) {
          if (!ignore) {
            console.error('Error fetching course preview:', err);
            setError('Failed to load course preview');
          }
        } finally {
          if (!ignore) {
            setLoading(false);
          }
        }
      };

      fetchCourseData();

      return () => {
        ignore = true;   // this run's result no longer matters once a newer one starts
      };
  }, [courseId, instanceId, refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <LoadingSkeleton />
      </Box>
    );
  }

  if (error || !courseData) {
    return (
      <Container>
        <Typography variant="h6" color="error" align="center">
          {error || 'Course not found'}
        </Typography>
      </Container>
    );
  }

  const { 
    course, 
    activities, 
    instance, 
    instances, 
    progress, 
    enrolled, 
    enrolledObj, 
    prerequisites, 
    requiresApproval, 
    approvalStatus, 
    author, 
    additionalAuthors, 
    enrollmentCount, 
    userRole,
    attachments,
    job_Aids 
  } = courseData;
  // Determine if the user is an admin or author
  const isAdminOrAuthor = userRole && [1, 11, 5, 7].includes(userRole);

  // Determine if prerequisites are met
  const prerequisitesMet = !prerequisites || prerequisites.length === 0 || 
    prerequisites.every(p => p.status === '1');
  
  // Get enrollment status message
  const getEnrollmentStatusMessage = () => {
    if (enrolled) return "Enrolled";
    if (approvalStatus?.status === 'pending') return "Approval Pending";
    if (!prerequisitesMet) return "Complete Prerequisites First";
    if (requiresApproval) return "Requires Manager Approval";
    return "Enroll Now";
  };
  
  // Determine if the enroll button should be disabled
  const isEnrollButtonDisabled = enrolled || 
    approvalStatus?.status === 'pending' || 
    !prerequisitesMet;

  return (
    <Routes>
    <Route path="instances" element={
      loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <LoadingSkeleton />
        </Box>
      ) : (
        <AllInstances 
          courseId={courseId} 
          instances={instances} 
          enrolledInstanceId={enrolledObj?.instance_id}
          progress={progress}
        />
      )
    } />
    <Route path="*" element={
      <Box sx={{ width: '100%', mt: -3 }}>
        {/* Only show tabs for non-learners (role_id !== 2). N44 pt 1.1: a comment again, inside braces; written after
            them, it showed on the page as text. */}
        {user?.role_id !== 2 && <PageTabs tabs={courseTabs} />}
        <CourseHeader 
          course={course} 
          progress={progress} 
          enrollmentCount={enrollmentCount} 
          userRole={userRole}
          activities={activities}
          isLearnerView={!isAdminOrAuthor}
          enrolled={enrolled}
          enrollButtonDisabled={isEnrollButtonDisabled}
          enrollStatusMessage={getEnrollmentStatusMessage()}
          requiresApproval={requiresApproval}
          prerequisitesMet={prerequisitesMet}
        />
        
        <Container maxWidth="lg">
          <Grid container spacing={3}>        
            {/* Left column - 8/12 width */}
            <Grid item xs={12} md={8}>
              {prerequisites && prerequisites.length > 0 && (
                <CoursePrerequisites 
                  prerequisites={prerequisites} 
                  parentCourseId={courseId}
                  parentCourseName={course.name}
                />
              )}
              
              {/* Always show all modules, but lock them if not enrolled and not admin */}
              <CourseModules 
                activities={activities} 
                courseId={courseId} 
                progress={progress} 
                enrolled={enrolled && instance?.id === enrolledObj?.instance_id}
                isAdminOrAuthor={isAdminOrAuthor}
                showLockStatus={!enrolled && !isAdminOrAuthor}
                prerequisitesMet={prerequisitesMet}
                requiresApproval={requiresApproval}
                contentOrdered={course?.content_ordered}
                refreshOnClose={refreshCourseData}
              />
            </Grid>
            
            {/* Right column - 4/12 width */}
            <Grid item xs={12} md={4}>
              <CourseEnrollment 
                course={course}
                instance={instance}
                enrolled={enrolled}
                enrolledObj={enrolledObj}
                progress={progress}
                activities={activities}
                prerequisites={prerequisites}
                requiresApproval={requiresApproval}
                approvalStatus={approvalStatus}
                userRole={userRole}
                prerequisitesMet={prerequisitesMet}
                showEnrollBtn={isAdminOrAuthor}
                refreshOnComplete={refreshCourseData}
              />
              
              <CourseDetails 
                course={course}
                instance={instance}
                instances={instances}
                author={author}
                additionalAuthors={additionalAuthors}
                progress={progress}
                activities={activities}
                enrolled={enrolled}
                enrolledObj={enrolledObj}
                attachments={attachments}
                job_Aids={job_Aids}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    } />
    </Routes>  
  );
};

export default CoursePreview;